import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Table from '../../../components/Table/Table';
import TablePagination from '../../../components/TablePagination/TablePagination';
import { Banner, BannerStatus } from '../../../models/BannersModels';
import BannersTableFilters from './BannersTableFilters';
import BannerPreviewModal from './BannerPreviewModal';

import useBannersTable from './useBannersTable';

function BannersTable() {
  const [t] = useTranslation();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<BannerStatus | 'ALL'>('ALL');
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const { headers, rows, totalBanners, isLoading } = useBannersTable({
    query,
    statusFilter,
    activePage,
    pageSize,
    onPreview: setPreviewBanner,
  });

  /**
   * On pagination change
   * @param page
   * @param pSize
   */
  const handlePaginationChange = (page: number, pSize: number) => {
    setActivePage(page);
    setPageSize(pSize);
  };

  /**
   * On search change
   * @param value
   */
  const handleSearch = (value: string) => {
    setActivePage(1);
    setQuery(value);
  };

  /**
   * On status filter change
   * @param status
   */
  const handleStatusFilterChange = (status: BannerStatus | 'ALL') => {
    setActivePage(1);
    setStatusFilter(status);
  };

  return (
    <Box paddingBottom="3rem">
      <BannersTableFilters
        onSearch={handleSearch}
        isLoading={isLoading}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
      />
      <Typography variant="body1" paddingBottom="1rem">
        {t('common.table.totalItems', { count: totalBanners })}
      </Typography>
      <Table headers={headers} rows={rows} isLoading={isLoading} />
      <TablePagination
        activePage={activePage}
        pageSize={pageSize}
        totalItems={totalBanners}
        handlePageChange={handlePaginationChange}
      />
      <BannerPreviewModal
        isOpen={Boolean(previewBanner)}
        onClose={() => setPreviewBanner(null)}
        banner={previewBanner}
      />
    </Box>
  );
}

export default BannersTable;
